import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import {
    MERGE_THEMING_AND_LOCALIZED_FILE_PROPERTY_TYPE_ID,
    LOCALIZE_STRINGS_PROPERTY_TYPE_ID,
    AXNUI_CONTENT_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { useModalContext } from "@context/use-modal-context";
import { AXNUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { AttributeClassificationTypes } from "@enums/attribute-classification-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import { AttributesValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { AttributeLabel, AttributeType, RpServiceAttributeProperty } from "@idw-types/data/reference-data/generic";
import { getUpdatedPropertiesForContentPropertyUpdate } from "@utils/content-util";
import { sortNumbers } from "@utils/number-util";
import { getInheritedAndMergedStringPropertyValue } from "@utils/verification-service-details-util";
import { AddRpServiceConfigurationAttributeModal } from "@containers/modals/add-rp-service-configuration-attribute-modal/add-rp-service-configuration-attribute-modal";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { SortableAttributesContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-attributes-tab/sortable-attributes-container/sortable-attributes-container";
import commonStyles from "../verification-service-details-component.less";
import styles from "./verification-service-details-attributes-tab.less";

type VerificationServiceDetailsAttributesTabContainerProps = {
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    isUsingThemedUI: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    axnuiContent: AXNUIContent;
    validationErrors: AttributesValidationErrors;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    attributeLabels: Array<AttributeLabel>;
    serviceAttributeProperties: Array<RpServiceAttributeProperty>;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const VerificationServiceDetailsAttributesTabContainer = (props: VerificationServiceDetailsAttributesTabContainerProps) => {
    const {
        isDisabled,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        isUsingThemedUI,
        multiLanguageAXNUIContent,
        axnuiContent,
        validationErrors,
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        attributeLabels,
        serviceAttributeProperties,
        setRpServiceConfigurationState
    } = props;
    const modalContext = useModalContext();

    const permissionBasedIsDisabled = useMemo(() => {
        return (
            isDisabled ||
            !rpServiceConfigurationState.permissions.canModifyAttributeProperties ||
            !rpServiceConfigurationState.permissions.canModifyBranding
        );
    }, [
        isDisabled,
        rpServiceConfigurationState.permissions.canModifyAttributeProperties,
        rpServiceConfigurationState.permissions.canModifyBranding
    ]);

    const filteredAttributeIds = useMemo(() => {
        return rpServiceConfigurationState.attributes
            .filter(attr => [
                AttributeClassificationTypes.PII,
                AttributeClassificationTypes.DEVICES,
                AttributeClassificationTypes.PREFERENCES,
                AttributeClassificationTypes.ORGANIZATION,
                AttributeClassificationTypes.OTHER,
                AttributeClassificationTypes.GOVERNMENT_ISSUED,
                AttributeClassificationTypes.CUSTOM
            ].includes(attr.attributeClassification?.name))
            .map(attr => attr.attributeType?.id);
    }, [rpServiceConfigurationState.attributes]);

    const selectedAttributes = useMemo(() => {
        return rpServiceConfigurationState.attributes
            .filter(attribute => filteredAttributeIds.includes(attribute.attributeType?.id) && attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE)
            .sort((a, b) => sortNumbers(a.order, b.order))
    }, [rpServiceConfigurationState.attributes, filteredAttributeIds]);

    const unselectedAttributes = useMemo(() => {
        const selectedAttributeIds = selectedAttributes.map(sa => sa.attributeType?.id).filter(x => x);
        const finalUnselectedAttributes: Array<FormattedRpServiceConfigurationAttribute> = [];

        rpServiceConfigurationState.attributes.forEach(attribute => {
            //check that it's in the filtered list, not in the selected list, and not in the unselected list already
            if (
                filteredAttributeIds.includes(attribute.attributeType?.id) &&
                !selectedAttributeIds.includes(attribute.attributeType?.id) &&
                !finalUnselectedAttributes.some(unselectedAttribute => unselectedAttribute.attributeType?.id === attribute.attributeType?.id)
            ) {
                finalUnselectedAttributes.push(attribute);
            }
        });
        return finalUnselectedAttributes;
    }, [selectedAttributes, rpServiceConfigurationState.attributes, filteredAttributeIds]);

    const addRpServiceConfigurationAttribute = (
        rpServiceConfigurationAttribute: FormattedRpServiceConfigurationAttribute,
        labelForTheming: string
    ) => {
        const newAttributes = rpServiceConfigurationState.attributes.map(attribute => attribute.uuid === rpServiceConfigurationAttribute.uuid
            ? rpServiceConfigurationAttribute
            : attribute
        );
        let newProperties = rpServiceConfigurationState.properties;
        if (isUsingThemedUI) {
            const newAXNUIContent: AXNUIContent = {
                ...axnuiContent,
                labels: {
                    ...axnuiContent.labels,
                    attributes: {
                        ...axnuiContent.labels.attributes,
                        [rpServiceConfigurationAttribute.attributeType.name]: labelForTheming
                    }
                }
            };

            newProperties = getUpdatedPropertiesForContentPropertyUpdate(
                AXNUI_CONTENT_PROPERTY_TYPE_ID,
                rpServiceConfigurationState.properties,
                newAXNUIContent,
                multiLanguageAXNUIContent,
                availableLanguages,
                selectedLanguage,
                isMultiLanguageEnabled,
                DEFAULT_AXNUI_CONTENT
            );
        }

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            attributes: newAttributes,
            properties: newProperties
        });
    };

    let maxOrder = 0;
    selectedAttributes.forEach(attr => {
        if (!isNaN(attr.order) && attr.order >= maxOrder) {
            maxOrder = attr.order + 1;
        }
    });

    const onClickAddAttribute = () => {
        const renderAddAttributeModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationAttributeModal
                    rpServiceConfigurationAttributes={unselectedAttributes}
                    isUsingThemedUI={isUsingThemedUI}
                    canModifyBranding={rpServiceConfigurationState.permissions.canModifyBranding}
                    maxOrder={maxOrder}
                    addRpServiceConfigurationAttribute={addRpServiceConfigurationAttribute}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddAttributeModal,
            modalType: ModalTypes.AddAttribute
        });
    };

    const attributesHeaderButton = (
        (!isDisabled) ? (
            <Button id="add-attribute" disabled={unselectedAttributes.length === 0} onClick={onClickAddAttribute}>
                {resources.pages.verificationServiceDetails.tabs.attributes.buttons.addAttribute.label}
            </Button>
        ) : null
    );

    const onClickRemoveAttribute = (attributeToRemove: FormattedRpServiceConfigurationAttribute) => {
        const newAttributes = rpServiceConfigurationState.attributes.map(attr => {
            // if we find the attribute to be removed
            if (attr.uuid === attributeToRemove.uuid) {
                // if the attribute exists in the database, set it to inactive
                if (attributeToRemove.id) {
                    return {
                        ...attributeToRemove,
                        status: EntityStatusTypes.INACTIVE
                    };
                } else {
                    // otherwise, return the attribute to its initial state
                    const initialAttribute = initialRpServiceConfiguration.attributes.find(initialAttribute => initialAttribute.uuid === attr.uuid);
                    if (initialAttribute) {
                        return {
                            ...initialAttribute,
                            addedToService: false,
                            status: EntityStatusTypes.INACTIVE
                        };
                    }
                }
            }
            return attr;
        });
        let newProperties = rpServiceConfigurationState.properties;
        //remove customization from theming as well
        if (isUsingThemedUI) {
            const newAXNUIContent: AXNUIContent = {
                ...axnuiContent,
                labels: {
                    ...axnuiContent.labels,
                    attributes: {
                        ...axnuiContent.labels.attributes,
                        [attributeToRemove.attributeType.name]: undefined
                    }
                }
            };

            newProperties = getUpdatedPropertiesForContentPropertyUpdate(
                AXNUI_CONTENT_PROPERTY_TYPE_ID,
                rpServiceConfigurationState.properties,
                newAXNUIContent,
                multiLanguageAXNUIContent,
                availableLanguages,
                selectedLanguage,
                isMultiLanguageEnabled,
                DEFAULT_AXNUI_CONTENT
            );
        }

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            attributes: newAttributes,
            properties: newProperties
        });
    };

    const setAttributes = (attributes: Array<FormattedRpServiceConfigurationAttribute>, updatedRpServiceConfigurationProperties: Array<FormattedRpServiceConfigurationProperty>) => {
        const newAttributes = rpServiceConfigurationState.attributes.map(originalAttribute => {
            const newAttribute = attributes.find(attribute => attribute.uuid === originalAttribute.uuid);
            if (newAttribute) {
                return newAttribute
            }

            return originalAttribute;
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            attributes: newAttributes,
            properties: updatedRpServiceConfigurationProperties || rpServiceConfigurationState.properties
        });
    };


    const getUpdatedPropertiesForAttributeLabelUpdate = (attributeLabel: AttributeLabel, attributeType: AttributeType) => {
        const updatedThemingContent: AXNUIContent = {
            ...axnuiContent,
            labels: {
                ...axnuiContent.labels,
                attributes: {
                    ...axnuiContent.labels.attributes,
                    [attributeType.name]: attributeLabel.label
                }
            }
        };

        const newProperties = getUpdatedPropertiesForContentPropertyUpdate(
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            rpServiceConfigurationState.properties,
            updatedThemingContent,
            multiLanguageAXNUIContent,
            availableLanguages,
            selectedLanguage,
            isMultiLanguageEnabled,
            DEFAULT_AXNUI_CONTENT
        );

        return newProperties;
    };

    const mergeThemingAndLocalizedFile = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            MERGE_THEMING_AND_LOCALIZED_FILE_PROPERTY_TYPE_ID
        );

        return isStringTrue(mergedValue);
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const localizeStrings = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            LOCALIZE_STRINGS_PROPERTY_TYPE_ID
        );

        return isStringTrue(mergedValue);
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const {
        canModifyAttributeLabels,
        propertiesToAdd
    } = useMemo(() => {
        let canModifyAttributeLabels = false;
        const propertiesToAdd: Array<string> = [];
        if (isUsingThemedUI && localizeStrings && mergeThemingAndLocalizedFile) {
            canModifyAttributeLabels = true;
        } else if (isUsingThemedUI && localizeStrings && !mergeThemingAndLocalizedFile) {
            propertiesToAdd.push("mergeThemingAndLocalizedFile");
        } else if (isUsingThemedUI) {
            canModifyAttributeLabels = true;
        }

        return {
            canModifyAttributeLabels,
            propertiesToAdd
        }
    }, [isUsingThemedUI, localizeStrings, mergeThemingAndLocalizedFile]);

    const attributeTabResources = resources.pages.verificationServiceDetails.tabs.attributes;

    return (
        <div className={styles.verificationServiceDetailsAttributesTab}>
            <div>
                <ContentPageHeader
                    title={attributeTabResources.title}
                    headerButton={attributesHeaderButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>
                        {attributeTabResources.description}
                    </span>
                    {validationErrors.attributesError}
                    {unselectedAttributes.length === 0 && (
                        <Alert variant={"info"}>{attributeTabResources.allAttributesMessage}</Alert>
                    )}
                    {selectedAttributes.length === 0 && (
                        <Alert variant={"info"}>{attributeTabResources.noAttributesMessage(permissionBasedIsDisabled)}</Alert>
                    )}
                    {!canModifyAttributeLabels && propertiesToAdd.length > 0 && (
                        <Alert variant={"warning"}>{attributeTabResources.canNotModifyLabels}</Alert>
                    )}
                    {selectedAttributes.length > 0 && (
                        <div className={styles.attributesContainer}>
                            {selectedAttributes.length > 0 && (
                                <SortableAttributesContainer
                                    isDisabled={isDisabled}
                                    localizeStrings={localizeStrings}
                                    mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                                    selectedLanguage={selectedLanguage}
                                    attributeLabels={attributeLabels}
                                    axnuiContent={axnuiContent}
                                    serviceAttributeProperties={serviceAttributeProperties}
                                    canModifyBranding={rpServiceConfigurationState.permissions.canModifyBranding}
                                    canModifyAttributeProperties={rpServiceConfigurationState.permissions.canModifyAttributeProperties}
                                    canViewAttributeProperties={rpServiceConfigurationState.permissions.canViewAttributeProperties}
                                    canModifyNonRestrictedAttributeProperties={rpServiceConfigurationState.permissions.canModifyNonRestrictedAttributeProperties}
                                    canViewNonRestrictedAttributeProperties={rpServiceConfigurationState.permissions.canViewNonRestrictedAttributeProperties}
                                    loginHintSupported={rpServiceConfigurationState.loginHintSupported}
                                    attributes={selectedAttributes}
                                    setAttributes={setAttributes}
                                    isUsingThemedUI={isUsingThemedUI}
                                    getUpdatedPropertiesForAttributeLabelUpdate={getUpdatedPropertiesForAttributeLabelUpdate}
                                    removeAttribute={onClickRemoveAttribute}
                                />
                            )}
                        </div>
                    )}
                </ContentPageBody>
            </div>
        </div>
    );
};